import {
  Chip,
  ChipColorEnum,
  ChipShapeEnum,
  ChipSizeEnum,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  IconButtonSizeEnum,
  Typography,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { TextHighlighter } from '@/components/TextHighlighter/TextHighlighter';
import { TProgramBroadcastSegmentsPatch } from '@/features/broadcast-schedule/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { usePatchSegment } from '@/features/segments/api/patch-segment';
import { TReviewStateValues, TSegment } from '@/features/segments/types';
import { downloadFromUrl } from '@/helpers/download';
import { ReturnDisclosureWithData } from '@/hooks/useDisclosureWithData';
import { useUnderMinDesktopWidth } from '@/hooks/useUnderMinDesktopWidth';
import { useProgramBroadcastFilters } from '@/stores/program-broadcast-filters';
import { useReport } from '@/stores/report-store';
import { StatusEnrichmentEnum } from '@/types/types';
import { HighlightStyles } from '@/utils/hightlights';

import { BroadcasterScheduleProgramMiniCardCardActions } from './BroadcasterScheduleProgramMiniCardCardActions';

type TBroadcasterScheduleProgramMiniCardCardBodyProps = {
  item: TSegment;
  segments?: boolean;
  relaunchDialog?: ReturnDisclosureWithData<string>;
};

export function BroadcasterScheduleProgramMiniCardCardBody({
  item,
  segments,
  relaunchDialog,
}: TBroadcasterScheduleProgramMiniCardCardBodyProps) {
  const { id = '' } = useParams();
  const { compactUis, setSegmentItems, setActiveSegment } = useBroadcastScheduleProgram();
  const { mutateAsync: patchSegment } = usePatchSegment({ id });
  const { setReport, setReportedId, setReportType } = useReport();
  const { search } = useProgramBroadcastFilters();
  const { underMinDesktopWith } = useUnderMinDesktopWidth();

  const handleStatusChange = (status: string) => {
    if (status === '5') {
      setReportType('segment');
      setReport(true);
      setReportedId(item.id);
      return;
    }
    const body: TProgramBroadcastSegmentsPatch = {
      ...item,
      id: item?.id || '',
      review_state: parseInt(status) as TReviewStateValues,
      uri: item?.uri || '',
      channel_id: item?.channel_id || '',
      media_type: item?.media_type || '',
      start_datetime: item?.start_datetime || '',
      end_datetime: item?.end_datetime || '',
      duration_in_seconds: item?.duration_in_seconds || 0,
      language_id: item?.language_id || '',
      programs: item?.programs.map(program => program.id) || [],
      segment_mentions: item?.segment_mentions || [],
      segment_transcriptions: item?.segment_transcriptions || [],
      segment_logos: item?.segment_logos || [],
      segment_peoples: item?.segment_peoples || [],
    };

    patchSegment(body).then(data => {
      setActiveSegment(prev => {
        if (!prev) return prev;

        if (prev.id === data.id) {
          return {
            ...prev,
            review_state: data.review_state,
          };
        }

        return prev;
      });
      setSegmentItems(prev => {
        if (!prev) return prev;
        return prev.map(segmentItem => {
          if (segmentItem.id === data.id) {
            return {
              ...segmentItem,
              review_state: data.review_state,
            };
          }
          return segmentItem;
        });
      });
    });
  };

  const [transcriptionToText, setTranscriptionToText] = useState<string>('');

  useEffect(() => {
    if (!item.parameters?.enrichments.stt) {
      return setTranscriptionToText('');
    }

    if (
      item.parameters?.status_enrichments.stt?.status === StatusEnrichmentEnum.PROCESSING ||
      item.parameters?.status_enrichments.stt?.status === StatusEnrichmentEnum.REPROCESSING
    ) {
      return setTranscriptionToText('Generando transcripción...');
    }

    if (item.parameters?.status_enrichments.stt?.status === StatusEnrichmentEnum.ERROR)
      return setTranscriptionToText('Error al generar la transcripción...');

    setTranscriptionToText(
      item.segment_transcriptions[0]?.asr_recognition_results[0]?.text ??
        'No se encontró transcripción...'
    );
  }, [item]);

  const uniqueKeywords = useMemo(() => {
    const list = (item.segment_mentions || []).flatMap(mention =>
      (mention.keywords || []).map(k => (k ?? '').trim()).filter(Boolean)
    );
    return Array.from(new Map(list.map(k => [k.toLowerCase(), k])).values());
  }, [item.segment_mentions]);

  return (
    <div className="flex w-full flex-1 flex-col">
      <div className="flex flex-col gap-1">
        {transcriptionToText && !compactUis && (
          <Typography
            fontSize={underMinDesktopWith ? FontSizeEnum.Sm : FontSizeEnum.Lg}
            color={FontColorEnum.Medium20}
            weight={FontWeightEnum.Bold}
          >
            <TextHighlighter
              styles={
                search
                  ? [
                      {
                        text: search,
                        className: HighlightStyles.SEARCH,
                      },
                    ]
                  : []
              }
            >
              {transcriptionToText}
            </TextHighlighter>
          </Typography>
        )}
        {!compactUis && uniqueKeywords.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {uniqueKeywords.map(keyword => (
              <Chip
                shape={ChipShapeEnum.ROUNDED}
                size={ChipSizeEnum.SM}
                color={ChipColorEnum.NEUTRAL_0}
                key={keyword}
                className="!rounded-md"
                textClassName="!whitespace-normal"
              >
                {keyword}
              </Chip>
            ))}
          </div>
        )}
      </div>
      <div
        data-id="card-actions"
        className={clsx(
          'flex animate-fade-in justify-end duration-300',
          compactUis
            ? 'h-0 w-0 group-hover/broadcasterCard:h-fit group-hover/broadcasterCard:w-fit group-hover/broadcasterCard:scale-100 md:scale-0'
            : 'group-hover/broadcasterCard:opacity-100 md:opacity-0'
        )}
      >
        <BroadcasterScheduleProgramMiniCardCardActions
          onDownload={downloadFromUrl}
          item={item}
          onReview={handleStatusChange}
          onAi={() => relaunchDialog?.open(item.id)}
          size={IconButtonSizeEnum.SM}
          className="bg-neutral-20"
          {...(!segments && { onDownload: downloadFromUrl })}
        />
      </div>
    </div>
  );
}
